import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const bottomRef = useRef(null);

  const loadThreads = () => api.get("/admin/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
  const loadThread = (uid) => api.get(`/admin/messages/${uid}`).then((r) => setMessages(r.data)).catch(() => {});

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const t = setInterval(() => loadThread(active), 6000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    try {
      await api.post(`/admin/messages/${active}`, { body: body.trim() });
      setBody("");
      loadThread(active);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activeUser = threads.find((t) => t.user.user_id === active)?.user;

  return (
    <div data-testid="admin-messages">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Messages</h1>
        <p className="text-sm text-slate-500 mt-2">Direct conversations with every client.</p>
      </FadeUp>

      <div className="mt-8 grid gap-6 lg:grid-cols-3 items-start">
        <FadeUp className="bg-white rounded-3xl border border-purple-100 p-4 space-y-2" data-testid="thread-list">
          {threads.map((t) => (
            <button key={t.user.user_id} onClick={() => setActive(t.user.user_id)} data-testid={`thread-${t.user.user_id}`}
              className={`w-full text-left p-4 rounded-2xl transition-colors ${active === t.user.user_id ? "bg-obsidian text-white" : "bg-lilac-mist hover:border-gold border border-transparent"}`}>
              <p className="text-sm font-medium truncate">{t.user.name}</p>
              <p className={`text-xs mt-1 truncate ${active === t.user.user_id ? "text-white/50" : "text-slate-400"}`}>{t.last_message}</p>
            </button>
          ))}
          {threads.length === 0 && <p className="text-sm text-slate-400 italic p-4">No conversations yet.</p>}
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-2 bg-white rounded-3xl border border-purple-100 flex flex-col h-[60vh]" data-testid="thread-view">
          {active ? (
            <>
              <p className="px-6 py-4 border-b border-purple-100 text-sm font-medium">{activeUser?.name} <span className="text-slate-400 font-normal">· {activeUser?.email}</span></p>
              <div className="flex-1 overflow-y-auto slim-scroll p-6 space-y-4">
                {messages.map((m) => (
                  <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed ${
                      m.sender === "admin" ? "bg-obsidian text-white rounded-br-sm" : "bg-lilac-mist text-slate-700 rounded-bl-sm"
                    }`} data-testid={`admin-message-${m.message_id}`}>
                      {m.body}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="p-4 border-t border-purple-100 flex gap-3">
                <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply as Empress…" data-testid="admin-message-input"
                  className="flex-1 rounded-full border border-purple-100 px-5 py-3 text-sm focus:outline-none focus:border-purple-400" />
                <button type="submit" data-testid="admin-message-send"
                  className="w-12 h-12 rounded-full bg-gold text-obsidian flex items-center justify-center hover:brightness-110 transition-all shrink-0" aria-label="Send reply">
                  <Send size={17} />
                </button>
              </form>
            </>
          ) : (
            <p className="text-sm text-slate-400 italic text-center my-auto">Select a conversation.</p>
          )}
        </FadeUp>
      </div>
    </div>
  );
}
